import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { LESSON } from '@/constants/testIds';

const SearchVisualization = ({ array, step, lessonId }) => {
  if (!array || array.length === 0) return null;

  const { left, right, mid } = step || { left: -1, right: -1, mid: -1 };

  const getCellStyle = (index) => {
    const baseStyle = "w-16 h-16 flex items-center justify-center border-2 rounded-md text-xl font-code relative";

    if (step?.found && step?.result === index) {
      return `${baseStyle} border-green-500 bg-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.5)]`;
    }
    if (index === mid) {
      return `${baseStyle} border-purple-500 bg-purple-500/20 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.5)]`;
    }
    if (index === left) {
      return `${baseStyle} border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.5)]`;
    }
    if (index === right) {
      return `${baseStyle} border-[#007AFF] bg-[#007AFF]/10 text-[#007AFF] shadow-[0_0_15px_rgba(0,122,255,0.5)]`;
    }
    // Eliminated cells (outside search range)
    if (left !== -1 && right !== -1 && (index < left || index > right)) {
      return `${baseStyle} border-white/5 bg-[#1A1A20]/40 text-[#71717A]/50 line-through`;
    }
    return `${baseStyle} border-white/20 bg-[#1A1A20] text-white`;
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3 justify-center items-center relative pb-16">
        {array.map((value, index) => (
          <motion.div
            key={index}
            data-testid={LESSON.arrayCell(index)}
            className={getCellStyle(index)}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.03 }}
          >
            <div className="absolute -top-5 text-[10px] text-[#71717A] font-code">
              {index}
            </div>
            <div>{value}</div>
          </motion.div>
        ))}

        {/* Left Pointer */}
        {left !== undefined && left >= 0 && left < array.length && (
          <motion.div
            data-testid={LESSON.pointer('left')}
            className="absolute flex flex-col items-center"
            initial={{ x: left * 76, y: 80 }}
            animate={{ x: left * 76, y: 80 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ChevronDown className="w-6 h-6 text-[#FFB800]" />
            <span className="text-xs font-code font-bold text-[#FFB800] mt-1">L</span>
          </motion.div>
        )}

        {/* Mid Pointer */}
        {mid !== undefined && mid >= 0 && mid < array.length && (
          <motion.div
            data-testid={LESSON.pointer('mid')}
            className="absolute flex flex-col items-center"
            initial={{ x: mid * 76, y: 110 }}
            animate={{ x: mid * 76, y: 110 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ChevronDown className="w-6 h-6 text-purple-400" />
            <span className="text-xs font-code font-bold text-purple-400 mt-1">MID</span>
          </motion.div>
        )}

        {/* Right Pointer */}
        {right !== undefined && right >= 0 && right < array.length && (
          <motion.div
            data-testid={LESSON.pointer('right')}
            className="absolute flex flex-col items-center"
            initial={{ x: right * 76, y: 80 }}
            animate={{ x: right * 76, y: 80 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ChevronDown className="w-6 h-6 text-[#007AFF]" />
            <span className="text-xs font-code font-bold text-[#007AFF] mt-1">R</span>
          </motion.div>
        )}
      </div>

      {/* Metrics */}
      {step && (
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded-lg p-3 text-center">
            <div className="text-[#FFB800] text-xs font-code mb-1">LEFT</div>
            <div className="text-white text-xl font-code font-bold">{left >= 0 ? left : '-'}</div>
          </div>
          <div className="bg-[#0F0F12] border border-purple-500/20 rounded-lg p-3 text-center">
            <div className="text-purple-400 text-xs font-code mb-1">MID</div>
            <div className="text-white text-xl font-code font-bold">{mid >= 0 ? mid : '-'}</div>
          </div>
          <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded-lg p-3 text-center">
            <div className="text-[#007AFF] text-xs font-code mb-1">RIGHT</div>
            <div className="text-white text-xl font-code font-bold">{right >= 0 ? right : '-'}</div>
          </div>
        </div>
      )}

      {step?.result !== undefined && step.found !== undefined && (
        <div className={`border rounded-lg p-4 text-center ${
          step.found 
            ? 'bg-green-500/10 border-green-500/30' 
            : 'bg-red-500/10 border-red-500/30'
        }`}>
          <div className="text-[#A1A1AA] text-sm font-body mb-1">
            {step.found ? 'Target Found' : 'Result'}
          </div>
          <div className={`text-3xl font-code font-bold ${
            step.found ? 'text-green-400' : 'text-red-400'
          }`}>
            {step.found ? `Index ${step.result}` : step.result}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchVisualization;
